import { HubConnectionBuilder, HubConnection, HttpTransportType } from '@microsoft/signalr';
import {App, Plugin} from 'vue';
import { EventEmitter } from 'events';
import { Logger, LogLevel, Impact } from '@/app/shared/service/logger';
import { retry } from 'ts-retry';
import Guid from "@/app/shared/helpers/guid";

export enum EEventType {
    CreatePdaZoneTask = 'CreatePdaZoneTask',
    UpdatePdaZoneTask = 'UpdatePdaZoneTask',
    ValidateAndConvertGeometryTask = 'ValidateAndConvertGeometryTask',
    MapRequestCreatedEvent = 'MapRequestCreatedEvent',
    ResponderRespondedNotInvolvedToMapRequestEvent = 'ResponderRespondedNotInvolvedToMapRequestEvent',
    ResponderRespondedWithImklToMapRequestEvent = 'ResponderRespondedWithImklToMapRequestEvent',
    MapRequestConfirmationReceivedEvent = 'MapRequestConfirmationReceivedEvent',
    CreateMapRequestForOverlayTask = 'CreateMapRequestForOverlayTask',
    Pmkl2PackageAvailableEvent = 'Pmkl2PackageAvailableEvent',
    ValidateImklUploadTask = 'ValidateImklUploadTask',
    ImklDataOfMapRequestOrderedEvent = 'ImklDataOfMapRequestOrderedEvent',
    ValidateTestImklUploadTask = 'imklTestStatus',
    Printstatus = 'printstatus',
    CreateMapRequestForOverlayTaskKlimResponded = 'CreateMapRequestForOverlayTaskKlimResponded',
    UtilityNetworkAuthorityRegisteredEvent = 'UtilityNetworkAuthorityRegisteredEvent',
    AskForImpersonationEvent = 'AskForImpersonationEvent'
}

export class SignalRHub {
    private connection: string;
    private signalrHub: HubConnection;
    private eventEmitter: EventEmitter;
    private connected: boolean = false;

    private _debug = !!import.meta.env.VITE_SIGNALR_DEBUG;

    constructor(connection: string) {
        this.eventEmitter = new EventEmitter();
        this.connection = connection;

        if (!!import.meta.env.VITE_SIGNALR_URI) {
            this.connection = import.meta.env.VITE_SIGNALR_URI;
        }

        try {
            this.signalrHub = new HubConnectionBuilder()
                .withUrl(this.connection, {
                        skipNegotiation: true,
                        transport: HttpTransportType.WebSockets,
                    })
                .withAutomaticReconnect([0, 2000, 5000, 10000, 15000, 30000])
                .configureLogging(this._debug ? LogLevel.Debug : LogLevel.Error)
                .build();

            this.signalrHub.onclose((error) => {

                const logEntryError = {
                    message: error?.message || 'SignalR on close error',
                    baseUrl: window.location.origin,
                    method: 'SignalR.onclose',
                    url: window.location.href,
                    code: error?.name || 'SIGNALR_ONCLOSE',
                    status: 0
                };

                Logger.send({
                    logEventLevel: LogLevel.Error,
                    logImpact: Impact.Medium,
                    fileName: 'signalr.ts',
                    functionName: 'onClose',
                    id: '',
                    correlationId :  Guid.randomGuid(),
                    context: 'SignalRHub',
                    detailContext: 'SignalR connection closed',
                    error: logEntryError,
                });
                console.error('SignalR connection closed:', error);
                this.connected = false;
            });

            this.signalrHub.onreconnecting((error) => {
                const logEntryError = {
                    message: error?.message || 'SignalR reconnection error',
                    baseUrl: window.location.origin,
                    method: 'SignalR.onreconnecting',
                    url: window.location.href,
                    code: error?.name || 'SIGNALR_RECONNECT',
                    status: 0  
                };

                Logger.send({
                    logEventLevel: LogLevel.Warning,
                    logImpact: Impact.Medium,
                    fileName: 'signalr.ts',
                    functionName: 'onReconnecting',
                    id: '',
                    correlationId :  Guid.randomGuid(),
                    context: 'SignalRHub',
                    detailContext: 'SignalR reconnecting',
                    error: logEntryError,
                });

                console.warn('SignalR reconnecting...', error);
            });

            this.signalrHub.onreconnected((connectionId) => {
                console.log('SignalR reconnected:', connectionId);
                this.connected = true;
                this.eventEmitter.emit('connected');
            });
        }
        catch(e) {
            console.error('Signalr: unable to build hub');
        }
    }

    async start() {
        // possibility to disable signalr for local dev when using a mock server
        if (import.meta.env.VITE_DISABLE_SIGNALR) {
            console.log('SignalR disabled by VITE_DISABLE_SIGNALR');
            return;
        }
        try {
            await this.signalrHub.start()
            this.connected = true;
            this.eventEmitter.emit('connected');
        } catch (error) {
            console.error('SignalR failed to connect', {
                userAgent: navigator.userAgent,
                online: navigator.onLine,
                error,
            });
            
            await this.retryConnectionStart();
        }
    }

    async retryConnectionStart() {
        try {
            //soms loopt de eerste connectie met signalR mis, dus proberen we eerst nog eens opnieuw.
            await retry(
                async () => {
                    await this.start();
                },
                { delay: 2000, maxTry: 3 }
            );
        } catch (reason) {
            if (!!reason) return;
            Logger.send({
                logEventLevel: LogLevel.Error,
                logImpact: Impact.Medium,
                fileName: 'signalr.ts',
                functionName: 'start',
                id: '',
                correlationId :  Guid.randomGuid(),
                context: 'SignalRHub',
                detailContext: `Connection failed with user agent: ${navigator.userAgent}, and online: ${navigator.onLine}`,
                error: reason,
            });
        }
    }

    onConnected(fie: () => void) {
        if (this.connected) {
            fie();
        } else {
            this.eventEmitter.on('connected', () => {
                fie();
            });
        }
    }

    stop(redirectUrl: string = null) {
        this.signalrHub.stop().then(() => {
            if (redirectUrl) {
                window.location.assign(redirectUrl);
            }
        });
    }

    subscribeToContactNotifications(group: string) {
        if (!!group && this.connected) {
            this.signalrHub.invoke('SubscribeToContactNotifications', group).catch((reason) => {
                if (!!reason) return;
                Logger.send({
                    logEventLevel: LogLevel.Error,
                    logImpact: Impact.Medium,
                    fileName: 'signalr.ts',
                    functionName: 'subscribeToContactNotifications',
                    id: `group: ${group}`,
                    correlationId : '',
                    context: 'SignalRHub',
                    detailContext: 'Failed to subscribe',
                    error: reason,
                });
            });
        }
    }

    subscribeToOrganisationNotifications(organisationId: string) {
        if (!!organisationId && this.connected) {
            this.signalrHub.invoke('subscribeToOrganisationNotifications', organisationId).catch((reason) => {
                if (!!reason) return;
                Logger.send({
                    logEventLevel: LogLevel.Error,
                    logImpact: Impact.Medium,
                    fileName: 'signalr.ts',
                    functionName: 'subscribeToOrganisationNotifications',
                    id: `organisationId: ${organisationId}`,
                    correlationId : Guid.randomGuid(),
                    context: 'SignalRHub',
                    detailContext: 'Failed to subscribe',
                    error: reason,
                });
            });
        }
    }

    unSubscribeFromOrganisationNotifications(organisationId: string) {
        if (!!organisationId && this.connected) {
            this.signalrHub.invoke('unSubscribeFromOrganisationNotifications', organisationId).catch((reason) => {
                if (!!reason) return;
                Logger.send({
                    logEventLevel: LogLevel.Error,
                    logImpact: Impact.Medium,
                    fileName: 'signalr.ts',
                    functionName: 'unSubscribeFromOrganisationNotifications',
                    id: `organisationId: ${organisationId}`,
                    correlationId : Guid.randomGuid(),
                    context: 'SignalRHub',
                    detailContext: 'Failed to unsubscribe',
                    error: reason,
                });
            });
        }
    }

    subscribeToMapRequestNotifications(mapRequestId: string) {
        if (!!mapRequestId && this.connected) {
            this.signalrHub.invoke('subscribeToMapRequestNotifications', mapRequestId.toLowerCase()).catch((reason) => {
                if (!!reason) return;
                Logger.send({
                    logEventLevel: LogLevel.Error,
                    logImpact: Impact.Medium,
                    fileName: 'signalr.ts',
                    functionName: 'subscribeToMapRequestNotifications',
                    id: `mapRequest: ${mapRequestId}`,
                    correlationId : Guid.randomGuid(),
                    context: 'SignalRHub',
                    detailContext: 'Failed to subscribe',
                    error: reason,
                });
            });
        }
    }

    unSubscribeFromMapRequestNotifications(mapRequestId: string) {
        if (!!mapRequestId && this.connected) {
            this.signalrHub.invoke('unSubscribeFromMapRequestNotifications', mapRequestId).catch((reason) => {
                if (!!reason) return;
                Logger.send({
                    logEventLevel: LogLevel.Error,
                    logImpact: Impact.Medium,
                    fileName: 'signalr.ts',
                    functionName: 'unSubscribeFromMapRequestNotifications',
                    id: `mapRequest: ${mapRequestId}`,
                    correlationId : Guid.randomGuid(),
                    context: 'SignalRHub',
                    detailContext: 'Failed to unsubscribe',
                    error: reason,
                });
            });
        }
    }

    subscribeToCustomIdNotifications(customId: string, customType: string) {
        if (!!customId && this.connected) {
            this.signalrHub.invoke('subscribeToCustomIdNotifications', customId.toLowerCase()).catch((reason) => {
                if (!!reason) return;
                Logger.send({
                    logEventLevel: LogLevel.Error,
                    logImpact: Impact.Medium,
                    fileName: 'signalr.ts',
                    functionName: `subscribeToCustomNotifications_${customType}`,
                    id: `customId: ${customId}_${customType}`,
                    correlationId : Guid.randomGuid(),
                    context: 'SignalRHub',
                    detailContext: 'Failed to subscribe',
                    error: reason,
                });
            });
        }
    }

    unSubscribeFromCustomIdNotifications(customId: string, customType: string) {
        if (!!customId && this.connected) {
            this.signalrHub.invoke('unSubscribeFromCustomIdNotifications', customId).catch((reason) => {
                if (!!reason) return;
                Logger.send({
                    logEventLevel: LogLevel.Error,
                    logImpact: Impact.Medium,
                    fileName: 'signalr.ts',
                    functionName: `unSubscribeFromCustomNotifications_${customType}`,
                    id: `customId: ${customId}_${customType}`,
                    correlationId : Guid.randomGuid(),
                    context: 'SignalRHub',
                    detailContext: 'Failed to unsubscribe',
                    error: reason,
                });
            });
        }
    }

    subToEvent(eventType: EEventType, callback: (message: IMessage) => void) {
        this.subscribeTo(eventType, callback);
    }

    unsubFromEvent(eventType: EEventType) {
        this.unSubscribeFrom(eventType);
    }

    subscribeTo(action: string, method: (message: IMessage) => void) {
        this.signalrHub
            .on(action, (message: IMessage) => {
                if (this._debug) {
                    console.log(`signalr: ${action} ${JSON.stringify(message)}`);
                }
                method(message);
            });
    }

    unSubscribeFrom(action: string) {
        this.signalrHub.off(action);
    }
}

export const signalrHub = new SignalRHub('/hub');

export default {
    install(app: App) {
        app.config.globalProperties.$signalrHub = signalrHub;
    }
} as Plugin;

export function useSignalrHub(): SignalRHub {
    return signalrHub;
}
